import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Send, Download, Trash2, Copy, Link as LinkIcon, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import LineItemsEditor from "@/components/LineItemsEditor";
import MockupGallery from "@/components/MockupGallery";
import PaymentScheduleEditor from "@/components/PaymentScheduleEditor";
import StatusBadge from "@/components/StatusBadge";

const DEFAULT_TERMS = `Payment Terms:
- 50% non-refundable deposit required to secure booking
- Remaining balance due 15 days before the event
- If event is within 30 days of signing, full payment is required at time of signing
- Payment methods: Zelle, cash, card (3.5% processing fee applies to card)
- Final guest count must be confirmed no later than 5 days before the event

Cancellation Policy:
- All deposits are non-refundable
- Cancellations made after 5 days of signing require client to pay full remaining balance
- Rescheduling based on availability at the discretion of Sheesh Eatery & Lounge

Event Setup & Service:
- Sheesh arrives 30–60 minutes prior to event start time for setup
- Client must ensure clear access to event space (parking, unloading, power)
- Hookah services require a designated outdoor or well-ventilated area
- Damaged or missing equipment will be billed to the client

Liability & Indemnification:
- Sheesh Eatery & Lounge assumes no responsibility for accidents, damages, or injuries beyond scope of service
- Client agrees to indemnify and hold harmless Sheesh Eatery & Lounge, its employees, and affiliates from any claims, losses, or damages related to the event

Food Handling & Quality:
- All food is prepared fresh on the day of the event
- Sheesh is not responsible for food safety after the event is completed`;

const STATUSES = ["Draft", "Sent", "Viewed", "Signed", "Change Requested", "Cancelled"];

const blank = {
  client_name: "",
  client_phone: "",
  client_email: "",
  event_date: "",
  event_time: "",
  event_location: "",
  catering_food: false,
  catering_mocktail: false,
  catering_hookah: false,
  menu_description: "",
  guest_count: 0,
  per_person_price: 0,
  service_charge: 0,
  tax: 0,
  addons: "",
  payment_terms: DEFAULT_TERMS,
  internal_notes: "",
};

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "col-span-1 md:col-span-2" : ""} data-testid={testid}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-white/15 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white placeholder-white/25 transition-colors";
const boxInputCls =
  "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2.5 focus:border-[#C9963F] focus:outline-none text-white placeholder-white/25 transition-colors";

export default function ContractEditor({ mode }) {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/contracts/${id}`);
        setContract(data);
        const merged = { ...blank, ...data };
        if (!merged.line_items || merged.line_items.length === 0) {
          const seeded = [];
          if (Number(data.per_person_price) > 0) {
            seeded.push({ id: "lg_pp", label: "Per Person", kind: "per_person", quantity: 1, unit_price: Number(data.per_person_price) });
          }
          if (Number(data.service_charge) > 0) {
            seeded.push({ id: "lg_sc", label: "Service Charge", kind: "flat", quantity: 1, unit_price: Number(data.service_charge) });
          }
          if (seeded.length) merged.line_items = seeded;
        }
        // Seed installments from legacy deposit/balance on first load if empty.
        if (!merged.installments || merged.installments.length === 0) {
          const dep = Number(data.deposit_amount_computed) || 0;
          const bal = Number(data.balance_amount_computed) || 0;
          if (dep > 0 || bal > 0) {
            merged.installments = [
              {
                id: "lg_dep", label: "Deposit", amount: dep,
                due_date: null,
                paid: !!data.deposit_paid, paid_at: data.deposit_paid_at || null,
                method: data.deposit_method || "", payment_link: "",
              },
              {
                id: "lg_bal", label: "Remaining Balance", amount: bal,
                due_date: data.balance_due_date_computed || null,
                paid: !!data.balance_paid, paid_at: data.balance_paid_at || null,
                method: data.balance_method || "", payment_link: "",
              },
            ];
          }
        }
        setForm(merged);
      } catch {
        toast.error("Could not load contract");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const totals = useMemo(() => {
    const gc = Number(form.guest_count) || 0;
    const items = form.line_items || [];
    let sub = 0;
    if (items.length) {
      for (const it of items) {
        const up = Number(it.unit_price) || 0;
        if (it.kind === "per_person") sub += gc * up;
        else sub += (Number(it.quantity) || 0) * up;
      }
    } else {
      sub = gc * (Number(form.per_person_price) || 0) + (Number(form.service_charge) || 0);
    }
    const total = sub + (Number(form.tax) || 0);

    const installments = form.installments || [];
    if (installments.length) {
      const instSum = installments.reduce((a, i) => a + (Number(i.amount) || 0), 0);
      const paid = installments.reduce((a, i) => a + (i.paid ? Number(i.amount) || 0 : 0), 0);
      const outstanding = Math.max(total - paid, 0);
      const firstUnpaidDue = installments
        .filter((i) => !i.paid && i.due_date)
        .map((i) => i.due_date)
        .sort()[0] || "";
      const deposit = Number(installments[0]?.amount) || 0;
      const balance = Math.max(total - deposit, 0);
      return {
        subtotal: sub, total,
        deposit, balance,
        due: firstUnpaidDue,
        paid, outstanding,
        installmentsSum: instSum,
        installmentsMismatch: Math.abs(instSum - total) > 0.01,
        useInstallments: true,
      };
    }

    // Legacy 2-payment fallback
    const depRaw = form.deposit_amount;
    const deposit = depRaw === null || depRaw === undefined || depRaw === "" ? +(total * 0.5).toFixed(2) : Number(depRaw) || 0;
    const balance = Math.max(total - deposit, 0);
    let due = form.balance_due_date || "";
    if (!due && form.event_date) {
      try {
        const d = new Date(form.event_date + "T00:00:00");
        d.setDate(d.getDate() - 15);
        due = d.toISOString().slice(0, 10);
      } catch {}
    }
    const paid = (form.deposit_paid ? deposit : 0) + (form.balance_paid ? balance : 0);
    const outstanding = Math.max(total - paid, 0);
    return { subtotal: sub, total, deposit, balance, due, paid, outstanding, useInstallments: false };
  }, [form.guest_count, form.line_items, form.per_person_price, form.service_charge, form.tax, form.deposit_amount, form.balance_due_date, form.event_date, form.deposit_paid, form.balance_paid, form.installments]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (!form.client_name?.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!form.event_date) {
      toast.error("Event date is required");
      return;
    }
    setSaving(true);
    try {
      const items = (form.line_items || []).map((it) => ({
        id: it.id,
        label: it.label || "",
        kind: it.kind || "flat",
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price) || 0,
      }));
      const installments = (form.installments || []).map((it) => ({
        id: it.id,
        label: it.label || "",
        amount: Number(it.amount) || 0,
        due_date: it.due_date || null,
        paid: !!it.paid,
        paid_at: it.paid_at || null,
        method: it.method || "",
        payment_link: it.payment_link || "",
      }));
      const payload = {
        ...form,
        guest_count: Number(form.guest_count) || 0,
        per_person_price: items.length ? 0 : (Number(form.per_person_price) || 0),
        service_charge: items.length ? 0 : (Number(form.service_charge) || 0),
        tax: Number(form.tax) || 0,
        line_items: items,
        installments,
      };
      if (isEdit) {
        const { data } = await api.put(`/contracts/${id}`, payload);
        setContract(data);
        toast.success("Contract updated");
      } else {
        const { data } = await api.post("/contracts", payload);
        toast.success("Contract created");
        navigate(`/admin/contracts/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(status) {
    try {
      const { data } = await api.patch(`/contracts/${id}/status`, { status });
      setContract(data);
      toast.success(`Status set to ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  }

  async function sendNow() {
    try {
      const { data } = await api.post(`/contracts/${id}/send`);
      setContract(data.contract);
      const link = `${window.location.origin}/c/${data.public_token}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Marked as Sent. Customer link copied.");
    } catch {
      toast.error("Failed to send");
    }
  }

  async function copyLink() {
    if (!contract?.public_token) return;
    const link = `${window.location.origin}/c/${contract.public_token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  }

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/contracts/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheesh-contract-${(form.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download");
    }
  }

  async function downloadEventSheet() {
    try {
      const res = await fetch(`${API}/contracts/${id}/event-sheet.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sheesh-event-sheet-${(form.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download event sheet");
    }
  }

  async function deleteContract() {
    if (!window.confirm("Delete this contract? This cannot be undone.")) return;
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contract deleted");
      navigate("/admin/contracts", { replace: true });
    } catch {
      toast.error("Failed to delete");
    }
  }

  async function markPayment(kind, paid, method = "") {
    if (!isEdit) {
      toast.error("Save the contract first");
      return;
    }
    try {
      const { data } = await api.post(`/contracts/${id}/payment`, { kind, paid, method });
      setContract(data);
      setForm((f) => ({ ...f, ...data }));
      toast.success(`${kind === "deposit" ? "Deposit" : "Balance"} ${paid ? "marked paid" : "marked unpaid"}`);
    } catch {
      toast.error("Failed to update payment");
    }
  }

  async function markInstallmentPaid(instId, paid, method = "") {
    if (!isEdit) {
      toast.error("Save the contract first");
      return;
    }
    try {
      const { data } = await api.post(`/contracts/${id}/installments/${instId}/payment`, { paid, method });
      setContract(data);
      setForm((f) => ({ ...f, ...data }));
      toast.success(paid ? "Marked paid" : "Marked unpaid");
    } catch {
      toast.error("Failed to update payment");
    }
  }

  if (loading) return <div className="text-white/40">Loading…</div>;

  return (
    <div data-testid="contract-editor-page">
      <Link to="/admin/contracts" className="inline-flex items-center gap-2 text-white/50 hover:text-[#C9963F] text-sm mb-6">
        <ArrowLeft size={14} /> Back to contracts
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">
            {isEdit ? "Edit Contract" : "New Contract"}
          </div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">
            {form.client_name || (isEdit ? "Untitled" : "New Contract")}
          </h1>
          {isEdit && contract && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <StatusBadge status={contract.status} />
              {contract.viewed_at && (
                <span className="text-[10px] uppercase tracking-wider text-white/40">
                  Viewed {new Date(contract.viewed_at).toLocaleString()}
                </span>
              )}
              {contract.signed_at && (
                <span className="text-[10px] uppercase tracking-wider text-[#C9963F]">
                  Signed {new Date(contract.signed_at).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isEdit && (
            <>
              <button onClick={sendNow} className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-2 text-sm" data-testid="send-now-btn">
                <Send size={14} /> Send to Client
              </button>
              <button onClick={copyLink} className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2 text-sm" data-testid="copy-link-btn">
                <LinkIcon size={14} /> Copy Link
              </button>
              <button onClick={downloadPdf} className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2 text-sm" data-testid="download-pdf-btn">
                <Download size={14} /> PDF
              </button>
              {contract?.status === "Signed" && (
                <button onClick={downloadEventSheet} className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-2 text-sm" data-testid="download-event-sheet-btn" title="Staff copy · no pricing">
                  <ClipboardList size={14} /> Event Sheet
                </button>
              )}
              <button onClick={deleteContract} className="inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 px-3 py-2 text-sm" data-testid="delete-btn">
                <Trash2 size={14} />
              </button>
            </>
          )}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium hover:bg-[#A67A2D] px-4 py-2 text-sm disabled:opacity-50" data-testid="save-contract-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {isEdit && contract && (
        <div className="bg-[#0e0e0e] border border-white/10 p-4 mb-6 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 mr-2">Quick Status</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              data-testid={`set-status-${s.replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border transition-colors ${
                contract.status === s
                  ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10"
                  : "border-white/15 text-white/70 hover:border-white/40"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-8">
          <Section title="Client">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Client Name *" testid="field-client-name">
                <input className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="input-client-name" />
              </Field>
              <Field label="Phone" testid="field-client-phone">
                <input className={inputCls} value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} data-testid="input-client-phone" />
              </Field>
              <Field label="Email" full testid="field-client-email">
                <input className={inputCls} type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="input-client-email" />
              </Field>
            </div>
          </Section>

          <Section title="Event Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Event Date *" testid="field-event-date">
                <input type="date" className={boxInputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="input-event-date" />
              </Field>
              <Field label="Event Time" testid="field-event-time">
                <input type="time" className={boxInputCls} value={form.event_time} onChange={(e) => set("event_time", e.target.value)} data-testid="input-event-time" />
              </Field>
              <Field label="Event Location / Address" full testid="field-event-location">
                <input className={inputCls} value={form.event_location} onChange={(e) => set("event_location", e.target.value)} data-testid="input-event-location" />
              </Field>
            </div>
          </Section>

          <Section title="Catering">
            <div className="flex flex-wrap gap-3 mb-5">
              {[
                ["catering_food", "Food Catering"],
                ["catering_mocktail", "Mocktail Bar"],
                ["catering_hookah", "Hookah Service"],
              ].map(([k, label]) => (
                <label key={k} data-testid={`chk-${k}`} className={`cursor-pointer px-4 py-2.5 border text-sm select-none ${form[k] ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                  <input type="checkbox" className="hidden" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            <Field label="Menu / Package Description" testid="field-menu-description">
              <textarea rows={5} className={boxInputCls} value={form.menu_description} onChange={(e) => set("menu_description", e.target.value)} placeholder="e.g. Mediterranean buffet for 50 — kabobs, hummus, salads, baklava…" data-testid="input-menu-description" />
            </Field>
            <Field label="Add-ons / Service Type" testid="field-addons">
              <textarea rows={3} className={boxInputCls} value={form.addons} onChange={(e) => set("addons", e.target.value)} placeholder="e.g. Live chef station, custom cocktails, premium hookahs" data-testid="input-addons" />
            </Field>
          </Section>

          <Section title="Pricing">
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-5">
              <Field label="Guest Count" testid="field-guest-count">
                <input type="number" min="0" className={boxInputCls} value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} data-testid="input-guest-count" />
              </Field>
              <Field label="Tax ($)" testid="field-tax">
                <input type="number" step="0.01" min="0" className={boxInputCls} value={form.tax} onChange={(e) => set("tax", e.target.value)} data-testid="input-tax" />
              </Field>
            </div>
            <LineItemsEditor
              items={form.line_items || []}
              guestCount={form.guest_count}
              onChange={(items) => set("line_items", items)}
              testidPrefix="contract-li"
            />
          </Section>

          <Section title="Payment Schedule">
            <p className="text-xs text-white/40 mb-4">
              Define one or more installments. Paste your Square payment link per row so the client sees a "Pay with Square" button on their contract page and PDF.
            </p>

            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  if ((form.installments || []).length && !window.confirm("Replace the current schedule with 50 / 50?")) return;
                  const t = totals.total || 0;
                  const dep = +(t * 0.5).toFixed(2);
                  const bal = +(t - dep).toFixed(2);
                  let bdd = "";
                  if (form.event_date) {
                    try {
                      const d = new Date(form.event_date + "T00:00:00");
                      d.setDate(d.getDate() - 15);
                      bdd = d.toISOString().slice(0, 10);
                    } catch {}
                  }
                  set("installments", [
                    { id: `i_${Date.now()}_1`, label: "Deposit", amount: dep, due_date: null, paid: false, method: "", payment_link: "" },
                    { id: `i_${Date.now()}_2`, label: "Remaining Balance", amount: bal, due_date: bdd || null, paid: false, method: "", payment_link: "" },
                  ]);
                }}
                data-testid="seed-default-schedule-btn"
                className="text-[10px] uppercase tracking-wider border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2"
              >
                50 / 50 Preset
              </button>
              <button
                type="button"
                onClick={() => {
                  if ((form.installments || []).length && !window.confirm("Replace the current schedule with 3 equal payments?")) return;
                  const t = totals.total || 0;
                  const each = +(t / 3).toFixed(2);
                  const last = +(t - each * 2).toFixed(2);
                  set("installments", [
                    { id: `i_${Date.now()}_1`, label: "Deposit", amount: each, due_date: null, paid: false, method: "", payment_link: "" },
                    { id: `i_${Date.now()}_2`, label: "2nd Payment", amount: each, due_date: null, paid: false, method: "", payment_link: "" },
                    { id: `i_${Date.now()}_3`, label: "Final Payment", amount: last, due_date: null, paid: false, method: "", payment_link: "" },
                  ]);
                }}
                data-testid="seed-3-payments-btn"
                className="text-[10px] uppercase tracking-wider border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2"
              >
                3 Equal Payments
              </button>
              {(form.installments || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Clear all installments?")) set("installments", []);
                  }}
                  data-testid="clear-installments-btn"
                  className="text-[10px] uppercase tracking-wider border border-white/15 text-white/50 hover:border-red-400 hover:text-red-400 px-3 py-2"
                >
                  Clear All
                </button>
              )}
            </div>

            <PaymentScheduleEditor
              installments={form.installments || []}
              total={totals.total}
              onChange={(items) => set("installments", items)}
              onMarkPaid={markInstallmentPaid}
              showPaidControls={isEdit}
            />

            {!isEdit && (
              <div className="mt-4 text-xs text-white/40">Save the contract to enable payment tracking.</div>
            )}
          </Section>

          <Section title="Terms (editable)">
            <textarea
              rows={14}
              className={`${boxInputCls} font-mono text-xs leading-relaxed`}
              value={form.payment_terms}
              onChange={(e) => set("payment_terms", e.target.value)}
              data-testid="input-payment-terms"
            />
          </Section>

          <Section title="Mockup Pictures (visible to client)">
            <p className="text-xs text-white/40 mb-4">
              Attach reference images, mood boards, or setup mockups. The client will see these on their contract page.
            </p>
            <MockupGallery
              contractId={isEdit ? id : null}
              images={form.mockup_images || []}
              onChange={(imgs) => set("mockup_images", imgs)}
            />
          </Section>

          <Section title="Internal Notes (not visible to client)">
            <textarea rows={4} className={boxInputCls} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} data-testid="input-internal-notes" />
          </Section>
        </div>

        {/* Sidebar / summary */}
        <aside className="space-y-4">
          <div className="bg-[#0e0e0e] border border-white/10 p-6 sticky top-24" data-testid="pricing-summary">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-4">Summary</div>
            <Row label="Guests" value={form.guest_count || 0} />
            {(form.line_items || []).map((it, idx) => {
              const up = Number(it.unit_price) || 0;
              const qty = it.kind === "per_person" ? (Number(form.guest_count) || 0) : (Number(it.quantity) || 0);
              const line = qty * up;
              return <Row key={it.id || idx} label={it.label || (it.kind === "per_person" ? "Per Person" : "Item")} value={`$${line.toFixed(2)}`} />;
            })}
            {(!form.line_items || form.line_items.length === 0) && (
              <>
                <Row label="Per Person" value={`$${Number(form.per_person_price || 0).toFixed(2)}`} />
                <Row label="Service" value={`$${Number(form.service_charge || 0).toFixed(2)}`} />
              </>
            )}
            <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
            <Row label="Tax" value={`$${Number(form.tax || 0).toFixed(2)}`} />
            <div className="h-px bg-[#C9963F]/40 my-3" />
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">Total</span>
              <span className="font-serif-brand text-3xl text-[#C9963F]" data-testid="summary-total">
                ${totals.total.toFixed(2)}
              </span>
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 space-y-2" data-testid="summary-payments">
              {totals.useInstallments ? (
                <>
                  {(form.installments || []).map((inst, i) => (
                    <div key={inst.id || i} className="flex items-baseline justify-between text-sm">
                      <span className="text-white/50 truncate pr-2">
                        {inst.label || `Payment ${i + 1}`}
                        {inst.paid && <span className="text-[10px] text-[#C9963F] ml-1">● PAID</span>}
                      </span>
                      <span className="text-white">${Number(inst.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between text-sm pt-1">
                    <span className="text-white/50">Outstanding</span>
                    <span className={totals.outstanding === 0 ? "text-[#C9963F]" : "text-amber-300"} data-testid="summary-outstanding">
                      ${totals.outstanding.toFixed(2)}
                    </span>
                  </div>
                  {totals.installmentsMismatch && (
                    <div className="text-[10px] uppercase tracking-wider text-amber-300 pt-1" data-testid="summary-mismatch">
                      Installments do not match total
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-white/50">Deposit {form.deposit_paid && <span className="text-[10px] text-[#C9963F] ml-1">● PAID</span>}</span>
                    <span className="text-white">${totals.deposit.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-white/50">Balance {form.balance_paid && <span className="text-[10px] text-[#C9963F] ml-1">● PAID</span>}</span>
                    <span className="text-white">${totals.balance.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm pt-1">
                    <span className="text-white/50">Outstanding</span>
                    <span className={totals.outstanding === 0 ? "text-[#C9963F]" : "text-amber-300"} data-testid="summary-outstanding">
                      ${totals.outstanding.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
              {totals.due && (
                <div className="text-[10px] uppercase tracking-wider text-white/40 pt-1">
                  Next due {new Date(totals.due + "T00:00:00").toLocaleDateString()}
                </div>
              )}
            </div>

            {isEdit && contract?.public_token && (
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Customer link</div>
                <button
                  onClick={copyLink}
                  data-testid="sidebar-copy-link"
                  className="w-full flex items-center justify-between gap-2 bg-[#0a0a0a] border border-white/10 hover:border-[#C9963F] px-3 py-2 text-xs text-white/70 transition-colors"
                >
                  <span className="truncate">/c/{contract.public_token.slice(0, 12)}…</span>
                  <Copy size={12} />
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-[#0e0e0e] border border-white/10 p-6 sm:p-8 fade-up">
      <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-6">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 text-sm">
      <span className="text-white/50">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
